import React, { useEffect, useState } from 'react';
import { previewUserPurge, purgeUser } from '../services/tenantService';

export default function AccountPurgeReview({ user, onClose, onPurged }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPreview(null);
    setConfirmation('');
    setError('');
    previewUserPurge(user._id)
      .then(({ data }) => {
        if (data.userId !== user._id || typeof data.canPurge !== 'boolean'
          || !Array.isArray(data.records) || !Array.isArray(data.blockers) || !Array.isArray(data.scope)
          || (data.canPurge && (!data.previewToken || data.blockers.length))) {
          throw new Error('Invalid purge preview. No data has been removed.');
        }
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || err.message || 'Unable to preview purge. Please retry.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user._id, attempt]);

  const execute = async () => {
    if (!preview?.canPurge || confirmation !== user._id) return;
    if (!window.confirm(`Permanently purge account ${user._id} and the personal records listed in this preview?\n\nThis cannot be undone. Shared records and redacted audit events will remain. Backups and RSMIN are not changed.`)) return;
    setPurging(true);
    setError('');
    try {
      const { data } = await purgeUser(user._id, { confirmation, previewToken: preview.previewToken });
      if (data.outcome !== 'purged') throw new Error('Unexpected purge response. Reload the user list to verify the account before retrying.');
      onPurged(data);
    } catch (err) {
      setPreview(null);
      setConfirmation('');
      setError(err.response?.data?.message || err.message || 'Purge failed. Generate a fresh preview before retrying.');
    } finally {
      setPurging(false);
    }
  };

  const summary = {};
  preview?.records.forEach((record) => {
    const key = `${record.model}: ${record.action === 'delete' ? 'delete' : 'redact attribution / audit details'}`;
    summary[key] = (summary[key] || 0) + 1;
  });

  return (
    <section aria-label="Account purge review">
      <h4>Purge account and personal data</h4>
      <p>{user.email} - Account ID: <strong>{user._id}</strong></p>
      <p>This is permanent, not archival. Review every deletion and exclusion before continuing.</p>
      {loading && <p role="status">Preparing purge preview...</p>}
      {error && <p role="alert">{error}</p>}
      {preview && (
        <>
          <h5>Scope and exclusions</h5>
          <ul>{preview.scope.map((item) => <li key={item}>{item}</li>)}</ul>
          <h5>Planned changes</h5>
          <ul>{Object.entries(summary).map(([label, count]) => <li key={label}>{label} ({count})</li>)}</ul>
          <details>
            <summary>Exact affected records ({preview.records.length})</summary>
            <table className="admin-table">
              <thead><tr><th>Record type</th><th>Record ID</th><th>Tenant ID</th><th>Action</th><th>Fields to redact</th></tr></thead>
              <tbody>{preview.records.map((record) => (
                <tr key={`${record.model}:${record.id}`}>
                  <td>{record.model}</td><td>{record.id}</td><td>{record.tenantId || 'Platform'}</td>
                  <td>{record.action}</td><td>{record.fields.join(', ')}</td>
                </tr>
              ))}</tbody>
            </table>
          </details>
          {preview.blockers.length > 0 && (
            <div role="alert">
              <p>Purge blocked. No records will be deleted or redacted.</p>
              <ul>{preview.blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}</ul>
            </div>
          )}
          {preview.canPurge && (
            <>
              <label htmlFor="purge-account-id">Type the exact account ID to confirm permanent purge</label>
              <input id="purge-account-id" autoComplete="off" value={confirmation}
                disabled={purging} onChange={(event) => setConfirmation(event.target.value)} />
              <p>The preview expires after 10 minutes. Any change to affected records requires another review.</p>
              <button className="btn-danger btn-sm"
                disabled={purging || confirmation !== user._id} onClick={execute}>
                {purging ? 'Purging...' : 'Permanently purge account and personal data'}
              </button>
              {' '}
            </>
          )}
        </>
      )}
      <button className="btn-secondary btn-sm" disabled={loading || purging}
        onClick={() => setAttempt((value) => value + 1)}>Refresh purge preview</button>
      {' '}
      <button className="btn-secondary btn-sm" disabled={purging} onClick={onClose}>Cancel purge</button>
    </section>
  );
}
